import {
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import {
  MeetingAidFacilitationIcon,
  MeetingAidKnowIcon,
  MeetingAidParticipantsIcon,
  MeetingAidSecretaryIcon,
} from 'src/icons'
import { MeetingStepAidRole } from '../meetingStepAids'
import MeetingStepAidText from './MeetingStepAidText'

interface Props {
  role: MeetingStepAidRole
  // The aid's text, already translated
  text: string
}

// One colour per role, as an outline (Yohan, 27/09): the hues also differ in
// lightness, so they stay apart without colour vision
const roleStyles: Record<
  MeetingStepAidRole,
  {
    icon: IconType
    light: string
    dark: string
    border: string
    darkBorder: string
  }
> = {
  facilitation: {
    icon: MeetingAidFacilitationIcon,
    light: 'orange.600',
    dark: 'orange.300',
    border: 'orange.300',
    darkBorder: 'orange.700',
  },
  secretary: {
    icon: MeetingAidSecretaryIcon,
    light: 'blue.600',
    dark: 'blue.300',
    border: 'blue.300',
    darkBorder: 'blue.700',
  },
  participants: {
    icon: MeetingAidParticipantsIcon,
    light: 'green.600',
    dark: 'green.300',
    border: 'green.300',
    darkBorder: 'green.700',
  },
  know: {
    icon: MeetingAidKnowIcon,
    light: 'yellow.700',
    dark: 'yellow.300',
    border: 'yellow.400',
    darkBorder: 'yellow.600',
  },
}

/**
 * One aid of a meeting step (#136): its name on hover, its text on click.
 * The bubble lives in this browser only — nothing is written or synced, so
 * nobody else in the meeting sees it open (Yohan, 26/09).
 */
export default function MeetingStepAidButton({ role, text }: Props) {
  const { t } = useTranslation()
  const style = roleStyles[role]
  const Icon = style.icon
  const label = t(`MeetingStepAids.roles.${role}`)

  const colors = {
    color: style.light,
    borderColor: style.border,
    _dark: { color: style.dark, borderColor: style.darkBorder },
  }

  return (
    <Popover placement="bottom-start" isLazy>
      <Tooltip label={label} hasArrow openDelay={300}>
        <Box display="inline-flex">
          <PopoverTrigger>
            <IconButton
              aria-label={label}
              icon={<Icon size={17} />}
              size="sm"
              isRound
              variant="outline"
              borderWidth="1.5px"
              {...colors}
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>
      <Portal>
        <PopoverContent w="md" maxW="calc(100vw - 32px)">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverBody p={4}>
            <Flex align="center" gap={2} mb={3} pr={6}>
              <Flex
                w="26px"
                h="26px"
                borderRadius="full"
                borderWidth="1.5px"
                align="center"
                justify="center"
                {...colors}
              >
                <Icon size={15} />
              </Flex>
              <Text fontWeight="bold">{label}</Text>
            </Flex>
            <MeetingStepAidText text={text} />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
